<template>
    <v-row dense>
        <v-col cols="12" md="12">
            <v-card variant="outlined">
                <v-card-title>
                    <span class="font-weight-bold text-subtitle-2">
                        {{ $t('discount_conditions_and_calculation_basis') }}
                    </span>
                </v-card-title>
                <v-card-text class="text-body-1">
                    <v-row no-gutters class="pb-4">
                        <v-col cols="12" md="10">
                            <v-row no-gutters class="ga-2">
                                <v-radio-group v-model="discountForEdit.discountConditions.nature" hide-details>
                                    <v-radio
                                        v-for="type in discountConditionsTypes"
                                        :key="type.value"
                                        :value="type.value"
                                        :label="type.title"
                                    />
                                </v-radio-group>
                                <v-radio-group
                                    v-if="discountForEdit.discountConditions.nature === 'ON_EXACT_NIGHT'"
                                    v-model="discountForEdit.discountConditions.oneTimeDiscount"
                                    hide-details
                                >
                                    <v-radio :value="true" :label="$t('one_time_discount')" />
                                    <v-radio :value="false" :label="$t('multiple_discount')" />
                                </v-radio-group>
                            </v-row>
                        </v-col>
                    </v-row>
                    <v-row no-gutters class="pb-4 ga-2 align-start">
                        <div class="pt-4">
                            {{ isShowDays ? $t('discount_terms.min_days') : $t('discount_terms.min_nights') }}
                        </div>
                        <v-number-input
                            v-model="discountForEdit.discountConditions.minInterval"
                            :max="100"
                            :min="1"
                            hide-details
                            :step="1"
                            width="100"
                            max-width="100"
                            :label="$t('min')"
                        />
                        <div class="pt-4">
                            {{ isShowDays ? $t('discount_terms.max_days') : $t('discount_terms.max_nights') }}
                        </div>
                        <v-number-input
                            v-model="discountForEdit.discountConditions.maxInterval"
                            :max="100"
                            :min="1"
                            hide-details="auto"
                            :step="1"
                            width="100"
                            max-width="100"
                            :label="$t('max')"
                            :rules="[
                                maxIntervalRules(
                                    discountForEdit.discountConditions.minInterval,
                                    $t('validation.max_min')
                                ),
                            ]"
                        />
                        <template v-if="discountForEdit.discountConditions.nature === 'ON_EXACT_NIGHT'">
                            <div class="pt-4">
                                {{
                                    isShowDays
                                        ? $t('discount_terms.number_of_days')
                                        : $t('discount_terms.number_of_night')
                                }}*
                            </div>
                            <v-number-input
                                v-model="discountForEdit.discountConditions.numberOfNightsDays"
                                :max="100"
                                :min="1"
                                hide-details="auto"
                                :step="1"
                                width="100"
                                max-width="100"
                                :rules="[requiredRule('')]"
                            />
                        </template>
                        <template v-if="discountForEdit.discountConditions.nature === 'FROM_EXACT_NIGHT'">
                            <div class="pt-4">{{ $t('discount_terms.starting_from') }}*</div>
                            <v-number-input
                                v-model="discountForEdit.discountConditions.applicableNight"
                                :max="100"
                                :min="1"
                                hide-details="auto"
                                :step="1"
                                :disabled="
                                    discountForEdit.discountConditions.freeNightsStartingCondition !==
                                    'FROM_EXACT_NIGHT'
                                "
                                width="100"
                                max-width="100"
                                :rules="[
                                    requiredRule(''),
                                    minIntervalRules(
                                        discountForEdit.discountConditions.maxInterval,
                                        $t('validation.max_min')
                                    ),
                                ]"
                            />
                        </template>
                        <div class="pt-4">
                            {{
                                discountForEdit.discountConditions.nature === 'FROM_EXACT_NIGHT'
                                    ? $t('discount_terms.for_night_discount_is')
                                    : $t('discount_terms.discount_is')
                            }}*
                        </div>
                        <DiscountAmount v-model="discountForEdit" />
                    </v-row>
                    <v-radio-group
                        v-if="discountForEdit.discountConditions.nature === 'ON_EXACT_NIGHT'"
                        v-model="discountForEdit.discountConditions.freeNightsStartingCondition"
                        hide-details
                    >
                        <v-row no-gutters class="align-center">
                            <v-col class="flex-grow-0">
                                <v-radio
                                    value="FROM_EXACT_NIGHT"
                                    :label="`${isShowDays ? $t(`freeDaysStartingCondition.FROM_EXACT_NIGHT`) : $t(`freeNightsStartingCondition.FROM_EXACT_NIGHT`)}*`"
                                    class="custom-radio-btn"
                                />
                            </v-col>
                            <div class="pl-2">
                                <v-number-input
                                    v-model="discountForEdit.discountConditions.applicableNight"
                                    :max="100"
                                    :min="1"
                                    hide-details="auto"
                                    :step="1"
                                    :disabled="
                                        discountForEdit.discountConditions.freeNightsStartingCondition !==
                                        'FROM_EXACT_NIGHT'
                                    "
                                    width="100"
                                    :rules="[
                                        requiredRule(''),
                                        minIntervalRules(
                                            discountForEdit.discountConditions.maxInterval,
                                            $t('validation.max_min')
                                        ),
                                    ]"
                                />
                            </div>
                        </v-row>
                        <v-radio
                            value="ON_CHEAPEST_NIGHT"
                            :label="
                                isShowDays
                                    ? $t(`freeDaysStartingCondition.ON_CHEAPEST_NIGHT`)
                                    : $t(`freeNightsStartingCondition.ON_CHEAPEST_NIGHT`)
                            "
                        />
                    </v-radio-group>
                </v-card-text>
            </v-card>
        </v-col>
    </v-row>
</template>

<script setup lang="ts">
    import {contractServiceType} from '~/utils/types/types'

    const props = defineProps<{
        serviceType: string
    }>()

    const discountForEdit = defineModel<any>()

    const {t} = useI18n()

    const discountConditionsTypes = computed(() => {
        switch (props.serviceType) {
            case contractServiceType.ownHotel:
                return ['ON_EXACT_NIGHT', 'FROM_EXACT_NIGHT', 'ON_CHEAPEST_NIGHT'].map(el => ({
                    title: t(`discountConditionsTypes.${el}`),
                    value: el,
                }))
            case contractServiceType.ownCarRent:
                return ['ON_EXACT_NIGHT', 'FROM_EXACT_NIGHT'].map(el => ({
                    title: t(`discountDayConditionsTypes.${el}`),
                    value: el,
                }))
            default:
                return []
        }
    })

    const isShowDays = computed(() => {
        const availableServices = [contractServiceType.ownCarRent]
        return availableServices.includes(props.serviceType as contractServiceType)
    })
</script>
